/**
 * Compact events list for embedding on other pages (Places tab, right-rail
 * widget). Owns nothing — the caller passes the origin coordinate + limit
 * and this component runs its own `useNearbyEvents` query.
 */
import { Link } from 'react-router-dom';
import { CalendarClock, MapPin } from 'lucide-react';

import { useNearbyEvents } from '@/lib/api';
import { categoryMeta } from '@/pages/events/categories';
import { FriendAttendeesBadge } from '@/pages/events/components/FriendAttendeesBadge';
import type { PlaceEvent } from '@/types/api';

export interface EventsListProps {
  lat: number | null | undefined;
  lng: number | null | undefined;
  radiusKm?: number;
  limit?: number;
  /** Copy shown when there are zero results — override per surface. */
  emptyText?: string;
  /** Compact layout for right-rail widgets (smaller type, no cover). */
  variant?: 'default' | 'compact';
  /** When provided, render these events directly instead of running the
   *  internal `useNearbyEvents` fetch. Lets callers with their own
   *  multi-stage fallback (nearby → wider → country) feed us the winning
   *  dataset without re-fetching. `lat`/`lng`/`radiusKm` are ignored
   *  when this is set. */
  events?: PlaceEvent[];
}

export function EventsList({
  lat,
  lng,
  radiusKm = 25,
  limit = 10,
  emptyText = 'No upcoming events in this area yet.',
  variant = 'default',
  events: eventsOverride,
}: EventsListProps) {
  // When a caller passes `events`, skip the fetch entirely — nothing
  // to load, nothing to error on.
  const hasOverride = eventsOverride !== undefined;
  const { data, isLoading, error } = useNearbyEvents({
    lat: hasOverride ? null : lat,
    lng: hasOverride ? null : lng,
    radiusKm,
    limit,
  });

  if (!hasOverride && isLoading) {
    return <p className="text-sm text-muted-foreground">Looking for events…</p>;
  }
  if (!hasOverride && error) {
    return <p className="text-sm text-red-500">Could not load events.</p>;
  }
  const events = hasOverride ? eventsOverride : (data?.events ?? []);
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ul className={variant === 'compact' ? 'flex flex-col gap-2' : 'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
      {events.map((e) => {
        const iso = e.starts_at.includes('T') ? e.starts_at : e.starts_at.replace(' ', 'T') + 'Z';
        const when = new Date(iso);
        const whenText = isNaN(when.getTime())
          ? e.starts_at
          : new Intl.DateTimeFormat(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }).format(when);
        const meta = categoryMeta(e.category);
        // Compact variant kept as a tight text-only row for the
        // right-rail widget — swapping it to the hero style would blow
        // the sidebar out. Default variant uses the blog-item layout
        // (image strip + body) that `/events` and `/events/mine` use so
        // the Places → Events tab reads the same.
        if (variant === 'compact') {
          // Compact row: 44×44 cover thumb + 2-line clamped title + one
          // dot-separated metadata line. Chip is emoji-only (label hidden
          // by sr-only). Ticketmaster occasionally lists ticket-tier
          // suffixes after " | " — strip them defensively here too so
          // stale legacy rows don't blow the title width.
          const cleanTitle = String(e.title || '').split(' | ')[0].trim();
          // Short date like "Mon · Oct 19" fits the one-line metadata run
          // better than a full "Mon, Oct 19, 9:00 PM" string.
          const shortDate = isNaN(when.getTime())
            ? e.starts_at
            : new Intl.DateTimeFormat(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              }).format(when);
          // Line 2 (location): city only. The compact widget sits in a
          // narrow sidebar so a full venue name ("N.S.I.C. Grounds,
          // Okhla Exhibition Centre") truncates ugly. The city is enough
          // for a "which part of town" cue — the full venue lives on the
          // event detail page.
          const locationText = e.city || e.place_name || '';
          return (
            <li
              key={e.id}
              className="rounded-lg border border-border p-2 transition-colors hover:border-brand-300 hover:bg-surface-2/40"
            >
              <Link to={e.url} className="flex items-start gap-2.5">
                {/* Thumb — real cover when uploaded, else a tinted gradient
                    with the category emoji so every row has a visual
                    anchor. Fixed 44×44 keeps the row height stable. */}
                <div
                  className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md"
                  style={e.cover_url ? undefined : {
                    background: `linear-gradient(135deg, ${(meta?.color ?? '#33B0F2')}30 0%, ${(meta?.color ?? '#33B0F2')}0F 100%)`,
                  }}
                >
                  {e.cover_url ? (
                    <img src={e.cover_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-lg" aria-hidden>
                      {meta?.emoji ?? '📅'}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className="line-clamp-2 text-sm font-medium leading-snug text-ink"
                      title={cleanTitle}
                    >
                      {cleanTitle}
                    </div>
                    {meta ? (
                      <span
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px]"
                        style={{ borderColor: meta.color + '50', color: meta.color }}
                        title={meta.label}
                      >
                        <span aria-hidden>{meta.emoji}</span>
                        <span className="sr-only">{meta.label}</span>
                      </span>
                    ) : null}
                  </div>
                  {/* Two-line metadata: date on top, venue+distance below.
                      Each line truncates independently so a long venue
                      doesn't push the date off, and the calendar/pin
                      icons give instant scanability. */}
                  <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                    <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="truncate">{shortDate}</span>
                  </div>
                  {locationText ? (
                    <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                      <span className="truncate">{locationText}</span>
                    </div>
                  ) : null}
                </div>
              </Link>
            </li>
          );
        }

        // ── Default variant: blog-item card matching /events discover ──
        const accent = meta?.color || '#33B0F2';
        return (
          <li
            key={e.id}
            className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-colors hover:border-brand-300 hover:shadow-md"
          >
            <Link to={e.url} className="block">
              {/* Image strip — cover or category-tinted gradient placeholder */}
              <div
                className="relative aspect-[16/8] w-full overflow-hidden"
                style={e.cover_url ? undefined : {
                  background: `linear-gradient(135deg, ${accent}30 0%, ${accent}0F 70%, transparent 100%)`,
                }}
              >
                {e.cover_url ? (
                  <img src={e.cover_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="text-4xl opacity-60" aria-hidden>
                      {meta?.emoji ?? '📅'}
                    </span>
                  </div>
                )}
                {meta ? (
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                    <span aria-hidden>{meta.emoji}</span>
                    {meta.label}
                  </span>
                ) : null}
                {/* Distance badge — top-left overlay. Only renders when
                    the server attached `distance_km` (nearby/places
                    surfaces), so cards in the discover feed stay
                    uncluttered. Same dark translucent chip as the
                    category badge so the two read as a pair. */}
                {typeof e.distance_km === 'number' ? (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {e.distance_km} km
                  </span>
                ) : null}
              </div>
              {/* Body */}
              <div className="p-4">
                <h3 className="line-clamp-2 font-display text-base font-semibold text-ink">
                  {e.title}
                </h3>
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <div className="flex min-w-0 items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{whenText}</span>
                  </div>
                  <span className="shrink-0 font-medium text-ink-2">
                    {e.counts.going} going
                  </span>
                </div>
                {e.place_name ? (
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{e.place_name}</span>
                  </div>
                ) : null}
                {e.friend_attendees && e.friend_attendees.length ? (
                  <div className="mt-2">
                    <FriendAttendeesBadge event={e} />
                  </div>
                ) : null}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
